import { Euler, MathUtils, type Object3D, Quaternion, Vector2, Vector3 } from 'three';

import { listen, publish } from '@pkg/components/Sections/Profile/pubSub';
import {
    glDragDirection,
    glLiquidIDClick,
    glTouchEndTopic,
    glTouchLeaveTopic,
    glTouchMoveTopic,
    glTouchStartTopic,
} from '@pkg/components/Sections/Profile/pubSub/constants';

import { type EventData } from '../../globals/EventListeners';
import Renderer from '../../globals/Renderer';
interface RotatorParams {
    rootBack: Object3D;
    rootFront: Object3D;
    frontGroup: Object3D;
    backMesh: Object3D;
    glowMesh: Object3D;
}

export default class Rotator {
    renderer = Renderer.getInstance();
    rootBack: Object3D;
    rootFront: Object3D;
    frontGroup: Object3D;
    backMesh: Object3D;
    glowMesh: Object3D;

    _rootQuaternion = new Quaternion();
    _targetQuaternion = new Quaternion();
    _targetRootQuaternion = new Quaternion();

    private _mouseDown = false;
    private _dragDirection = 1;
    private _freeRotationDragRange = new Vector2(0.15, 0.15);
    private _mouseOnMouseDown = new Vector2();
    private _targetOnMouseDown = new Vector2(0, 0);
    private _dragTarget = new Vector2(0, 0);
    private _dragRange = 0.003;
    private _maxVelocity = 0.06;
    private _dragSpeed = 5;
    private _dragging = false;
    private _rotationX = 0;
    private _rotationY = 0;
    private _freeRotationQuaternion = new Quaternion();
    private _iconQuaternion = new Quaternion();
    private _targetIconQuaternion = new Quaternion();
    private _blendQuaternion = new Quaternion();
    private _blendSlerpValue = 0;
    private _isExpandedView = true;
    private _freeRotationDamping = 0.98;
    private _quatX = new Quaternion();
    private _quatY = new Quaternion();
    private _quatZ = new Quaternion();

    // velocity calculations
    private _diffY = 0;
    private _diffX = 0;

    constructor({ rootBack, rootFront, frontGroup, backMesh, glowMesh }: RotatorParams) {
        this.rootBack = rootBack;
        this.rootFront = rootFront;
        this.frontGroup = frontGroup;
        this.backMesh = backMesh;
        this.glowMesh = glowMesh;
        this.initListeners();
    }

    get expandedView() {
        return this._isExpandedView;
    }

    set expandedView(val: boolean) {
        this._isExpandedView = val;
    }

    set blendSlerpValue(value: number) {
        this._blendSlerpValue = value;
    }

    runSnap() {
        this._dragSpeed = 3;
        this.rotateRootToTarget(this._rootQuaternion);
    }

    initListeners() {
        listen(glTouchStartTopic, this.handleMouseDown.bind(this));
        listen(glTouchMoveTopic, this.handleMouseMove.bind(this));
        listen(glTouchEndTopic, this.handleMouseUp.bind(this));
        listen(glTouchLeaveTopic, this.handleMouseLeave.bind(this));
    }

    handleMouseLeave() {
        if (this._mouseDown && this._isExpandedView) {
            this.runSnap();
        }
        this._mouseDown = false;
    }

    handleMouseUp() {
        if (!this._dragging) {
            publish(glLiquidIDClick);
        }
        if (this._mouseDown && this._isExpandedView && this._dragging) {
            this.runSnap();
        }
        this._mouseDown = false;
    }

    handleMouseDown(ev: EventData) {
        this._dragging = false;
        this._mouseDown = true;
        if (this._isExpandedView) {
            this._mouseOnMouseDown.set(ev.raw.x, ev.raw.y);
        } else {
            this._mouseOnMouseDown.x = ev.normalized.x * this._freeRotationDragRange.x;
            this._mouseOnMouseDown.y = ev.normalized.y * this._freeRotationDragRange.y;
            this._targetOnMouseDown.x = this._dragTarget.x;
            this._targetOnMouseDown.y = this._dragTarget.y;
        }
    }

    handleMouseMove(ev: EventData) {
        this._dragging = true;

        if (this._mouseDown) {
            this._dragDirection = this._mouseOnMouseDown.x < ev.normalized.x ? 1 : -1;
            publish(glDragDirection, this._dragDirection);
        }

        if (this._isExpandedView) {
            const euler = new Euler();
            const deltaX = (ev.raw.x - this._mouseOnMouseDown.x) * this._dragRange;
            const deltaY = (ev.raw.y - this._mouseOnMouseDown.y) * this._dragRange;

            if (this._mouseDown) {
                this._dragSpeed = 10;
                euler.y += deltaX;
                euler.x += deltaY;
                this._targetRootQuaternion = new Quaternion().setFromEuler(euler);
            }
        } else {
            if (this._mouseDown) {
                const mouseX = ev.normalized.x * this._freeRotationDragRange.x;
                const mouseY = ev.normalized.y * this._freeRotationDragRange.y;
                this._dragTarget.x = this._targetOnMouseDown.x + (mouseX - this._mouseOnMouseDown.x);
                this._dragTarget.y = this._targetOnMouseDown.y - (mouseY - this._mouseOnMouseDown.y);
            }
        }
    }

    rotateRootToTarget(quat: Quaternion) {
        this._dragSpeed = 2;
        this._targetRootQuaternion = quat;
    }

    rotateToTarget(angle: Vector3) {
        this._quatX.setFromAxisAngle(new Vector3(1, 0, 0), angle.x * MathUtils.DEG2RAD);
        this._quatY.setFromAxisAngle(new Vector3(0, 1, 0), angle.y * MathUtils.DEG2RAD);
        this._quatZ.setFromAxisAngle(new Vector3(0, 0, 1), angle.z * MathUtils.DEG2RAD);

        const q = this._quatX.multiply(this._quatY);
        const q2 = q.multiply(this._quatZ);

        this._targetIconQuaternion.copy(q2);
    }

    rotateContinuous(val: number) {
        this._dragTarget.x += (val * 1.5) % (Math.PI * 2);
        this._dragTarget.y -= (val * 1) % (Math.PI * 2);
    }

    resetRootRotations() {
        this._dragTarget.setScalar(0);
    }

    set dragRange(val: number) {
        this._dragRange = val;
    }

    get rootVelocity() {
        return Math.abs(this._rotationX * 2 + this._rotationY * 2.5) * 0.5;
    }

    // TODO can we delete this unused var?
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _applyFreeRotation(delta: number) {
        this._rotationX += this._dragTarget.x * 0.15;
        this._rotationY += this._dragTarget.y * 0.15;

        this._dragTarget.x *= this._freeRotationDamping;
        this._dragTarget.y *= this._freeRotationDamping;

        const roundedX = Math.round(this._rotationX);
        this._diffX = roundedX - this._rotationX;
        this._rotationX += this._diffX * 0.2625;
        this._rotationX = Math.max(Math.min(this._maxVelocity, this._rotationX), -this._maxVelocity);

        const roundedY = Math.round(this._rotationY);
        this._diffY = roundedY - this._rotationY;
        this._rotationY += this._diffY * 0.2625;
        this._rotationY = Math.max(Math.min(this._maxVelocity, this._rotationY), -this._maxVelocity);

        // this._freeRotationQuaternion.copy(this.frontGroup.quaternion);

        // const axisX = new Vector3(1, 0, 0).applyQuaternion(this.frontGroup.quaternion.clone().invert()).normalize();
        // const quatWorldX = new Quaternion().setFromAxisAngle(axisX, (this._rotationY % Math.PI) * 2);
        // this._freeRotationQuaternion.multiply(quatWorldX);

        // const axisY = new Vector3(0, 1, 0).applyQuaternion(this.frontGroup.quaternion.clone().invert()).normalize();
        // const quatWorldY = new Quaternion().setFromAxisAngle(axisY, (this._rotationX % Math.PI) * 2);
        // this._freeRotationQuaternion.multiply(quatWorldY);
    }

    // TODO can we delete this unused var?
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    update({ elapsed, delta }) {
        // Used to calculate velocity of dragging to ripple cube faces
        this._applyFreeRotation(delta);

        this._iconQuaternion.slerp(this._targetIconQuaternion, delta * 4);

        this.frontGroup.quaternion.copy(this._iconQuaternion);
        this.backMesh.quaternion.copy(this._iconQuaternion);
        this.glowMesh.quaternion.copy(this._iconQuaternion);
        // }
    }
}
